interface JWTPayload {
  exp: number;
  user_id: string;
  email: string;
  role: string;
}

// Simple JWT decode function to avoid external dependency
function decodeJWT(token: string): JWTPayload {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}

class AutoLogoutManager {
  private inactivityTimer: any = null;
  private tokenExpiryTimer: any = null;
  private readonly INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours - disabled for testing
  private readonly WARNING_TIME = 30 * 1000; // 30 seconds before logout
  private warningTimer: any = null;
  private countdownInterval: any = null;
  private isWarningShown = false;
  // Flag to disable all inactivity checking and logging for testing
  private readonly DISABLE_INACTIVITY_FOR_TESTING = true;

  private logoutCallback: (() => void) | null = null;
  private warningCallback: ((timeLeft: number) => void) | null = null;

  constructor() {
    this.bindActivityListeners();
  }

  public initialize(logoutCallback: () => void, warningCallback?: (timeLeft: number) => void): void {
    this.logoutCallback = logoutCallback;
    this.warningCallback = warningCallback || null;
    this.startTokenExpiryTimer();

    // Only start inactivity timer if user is NOT an admin
    if (!this.isAdminUser()) {
      this.resetInactivityTimer();
    } else {
      console.log('Admin: Inactivity timer DISABLED for admin user');
    }
  }

  public cleanup(): void {
    this.clearAllTimers();
    this.unbindActivityListeners();
  }

  private isAdminUser(): boolean {
    const userData = localStorage.getItem('user');
    if (!userData) return false;

    try {
      const user = JSON.parse(userData);
      return user.role === 'admin' || user.role === 'superadmin';
    } catch {
      return false;
    }
  }

  private bindActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
  }

  private unbindActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  private handleActivity = (): void => {
    // Skip all inactivity checking and logging for testing
    if (this.DISABLE_INACTIVITY_FOR_TESTING) {
      return;
    }

    console.log('Admin: User activity detected');

    // Skip inactivity reset for admin users
    if (this.isAdminUser()) {
      console.log('Admin: Skipping inactivity reset for admin user');
      return;
    }

    this.resetInactivityTimer();
    this.clearWarning();
  };

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    console.log(`Admin: Resetting inactivity timer. Warning will show in ${(this.INACTIVITY_TIMEOUT - this.WARNING_TIME) / 1000} seconds`);
    this.inactivityTimer = setTimeout(() => {
      console.log('Admin: Inactivity timeout reached, showing warning');
      this.showWarning();
    }, this.INACTIVITY_TIMEOUT - this.WARNING_TIME);
  }

  private showWarning(): void {
    if (this.isWarningShown || !this.warningCallback) return;

    this.isWarningShown = true;
    let timeLeft = this.WARNING_TIME / 1000;

    this.warningCallback(timeLeft);

    this.countdownInterval = setInterval(() => {
      timeLeft--;
      this.warningCallback?.(timeLeft);

      if (timeLeft <= 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        this.performLogout();
      }
    }, 1000);

    this.warningTimer = setTimeout(() => {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this.performLogout();
    }, this.WARNING_TIME);
  }

  private clearWarning(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.isWarningShown = false;
  }

  private startTokenExpiryTimer(): void {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decoded = decodeJWT(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = (decoded.exp - currentTime) * 1000;

      if (timeUntilExpiry > 0) {
        this.tokenExpiryTimer = setTimeout(() => {
          this.performLogout('Token expired');
        }, timeUntilExpiry);
      } else {
        // Token already expired
        this.performLogout('Token expired');
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      this.performLogout('Invalid token');
    }
  }

  private clearAllTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private performLogout(reason?: string): void {
    console.log(`Auto logout triggered: ${reason || 'Inactivity timeout'}`);
    this.cleanup();
    this.logoutCallback?.();
  }

  public extendSession(): void {
    console.log('Admin: Extending session - Stay Logged In clicked');

    // Skip session extension for admin users (they don't have inactivity timers)
    if (this.isAdminUser()) {
      console.log('Admin: Skipping session extension for admin user');
      return;
    }

    this.clearWarning();
    this.resetInactivityTimer();
    this.startTokenExpiryTimer(); // Restart token expiry timer
  }

  public isTokenExpired(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return true;

    try {
      const decoded = decodeJWT(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      return true;
    }
  }
}

export const autoLogoutManager = new AutoLogoutManager();
